"use client";

import styles from "@/styles/account/register.module.scss";
import { useEffect, useState, useRef, useCallback, Suspense } from "react"
import { setCookie } from '@/utils/cookie'
import Image from "next/image";
import Link from "next/link";
import loginApi from '@/service/modules/loginApi'
import { Button, Input, Form, Divider, Checkbox } from 'react-vant'
import { useSearchParams } from 'next/navigation'
import { useDispatch } from 'react-redux'
import { setUserInfo } from '@/store/slices/userSlice'
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useGoogleLoginUtils } from '@/utils/googleLogin'; // 替换为实际的路径
import ReCAPTCHA from 'react-google-recaptcha';
import { matomoSetCustomDimension } from "@/utils/matomoEvent";

import { toast } from '@/components/global/Toast'

// 创建一个包含 useSearchParams 的组件
function SearchParamsWrapper({ children }) {
  const searchParams = useSearchParams();
  return children(searchParams);
}

// 将注册内容提取到单独的组件中，确保它在GoogleOAuthProvider内部使用
function RegistContent() {
  const [form] = Form.useForm()
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const dispatch = useDispatch();

  const [checkedWeeklyOffers, setCheckedWeeklyOffers] = useState(true);
  const [checkedDaily, setCheckedDaily] = useState(false);
  const [agreeTermsCondition, setAgreeTermsCondition] = useState(true);

  const [redirectUrl, setRedirectUrl] = useState('')
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
  const [passwordPreview, setPasswordPreview] = useState(false)
  const [comfirmPasswordPreview, setComfirmPasswordPreview] = useState(false)
  const [loading, setLoading] = useState(false);

  // 登录、注册成功，返回前页面
  const pageReturn = useCallback(() => {
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    } else {
      window.location.replace('/');
    }
  }, [redirectUrl])

  // 获取重定向地址信息 - 使用 Suspense 包裹 SearchParamsWrapper
  const handleSearchParams = useCallback((searchParams) => {
    const redirect = searchParams.get('redirect')

    // 存贮重定向地址
    if (redirect) {
      setRedirectUrl(redirect)
    }
  }, [])

  // 有token就认定为已经登录，立即跳转回去
  useEffect(() => {
    if (localStorage.getItem('userToken')) {
      pageReturn()
    }
  }, [pageReturn])

  // 获取信用卡支付错误次数以及Recaptcha key
  const getRecaptchaSiteKey = async() => {
    try {
      const res = await loginApi.LoginErrorCheck()
      if (res && res.data) {
        setRecaptchaSiteKey(res.data.siteKey || '')
      }
    } catch (error: unknown) {
      toast.show((error as Error).message);
    }
  }

  // 获取Recaptcha token
  const handleRecaptchaChange = (value: string | null) => {
    console.log('Recaptcha token:', value);
  };

  // 获取Recaptcha key
  useEffect(() => {
    getRecaptchaSiteKey()
  }, [])

  // 去查看条款
  const hendleViewTerms = () => {
    window.location.href = '/article/TermsAndConditions'
  };

  // 订阅checkbox触发器
  const onChangeWeeklyOffers = (value) => {
    setCheckedWeeklyOffers(value)
  };
  // 订阅checkbox触发器
  const onChangeDaily = (value) => {
    setCheckedDaily(value)
  };

  // 订阅checkbox触发器
  const onChangeAgreeTermsCondition = (value) => {
    setAgreeTermsCondition(value)
  };

  const onFinish = async(values: {email: string; password: string; confirmPassword: string}) => {
    try {
      if (loading) {
        return
      }

      if (!agreeTermsCondition) {
        toast.show('Please agree to the terms and conditions')
        return
      }

      const gRecaptchaResponse = recaptchaRef.current?.getValue();
      if (recaptchaSiteKey && !gRecaptchaResponse) {
        toast.show('Please verify you are not a robot')
        return
      }

      setLoading(true)

      const dataObj = {
        email: values.email.trim(),
        password: window.btoa(values.password),
        password_confirmation: window.btoa(values.confirmPassword),
        subscribed: checkedWeeklyOffers ? 1 : 0,
        subscribed_crazydeal: checkedDaily ? 1 : 0,
        recaptcha: ''
      }

      if (recaptchaSiteKey) {
        dataObj.recaptcha = gRecaptchaResponse
      }

      const finnalParams = Object.fromEntries(
        Object.entries(dataObj).filter(([, value]) => value !== null && value !== '')
      );

      const res = await loginApi.register(finnalParams)
      setLoading(false)
      if (res.data) {
        toast.show('Regist Successful!');

        // matomo记录注册结果
        const registerResult = 0
        matomoSetCustomDimension(22, registerResult)

        // userEmail存起来，方便数据埋点使用
        if (res.data.email) {
          localStorage.setItem('userEmail', res.data.email || '')
          setCookie('userCustomerId', res.data.customerID || '')
        } else {
          localStorage.removeItem('userEmail')
        }

        // 将用户信息存入Redux
        dispatch(setUserInfo(res.data))

        // 注册成功，跳转
        setTimeout(() => {
          pageReturn()
        }, 1500);
      }
    } catch (error: unknown) {
      setLoading(false)
      /* 处理错误信息 
        - 假设API错误的返回格式为：{ message: '错误信息', field: '表单字段名' }
        - 你需要根据你实际的错误响应结构来调整
      */

      interface ApiError {
        message: string;
        code: number;
        response?: unknown;
      }
      
      if ((error as ApiError).message) {
        if ((error as ApiError).code == 110000) {
          // 已经登录
          const errorMessage = (error as ApiError).message
          const errorField = 'email'

          form.setFields([
            {
              name: errorField,
              errors: [errorMessage],
            }
          ]);
        } else if ((error as ApiError).code == 115003) {
          const currentEmail = localStorage.getItem('userEmail')
          alert(`You are already logged in to your account (${currentEmail}) in another tab.\nPlease log in to the same account in the current tab, or log out of your account (${currentEmail}) and try again.`)

          setTimeout(() => {
            pageReturn()
          }, 500);
        } else {
          // 其他可能的错误，都放在confirmPassword表单上（因为它是最后一个表单）
          const errorMessage = (error as ApiError).message
          const errorField = 'confirmPassword'

          form.setFields([
            {
              name: errorField,
              errors: [errorMessage],
            }
          ]);
        }
      } else {
        // 如果不是一个有效的数据响应，你可以设置一个通用的错误信息
        toast.show('An unexpected error occurred. Please try again.');
      }

      // matomo记录注册结果
      const registerResult = (error as ApiError).code
      matomoSetCustomDimension(22, registerResult)
    }
  }

  // Google登录 - 确保在GoogleOAuthProvider内部调用
  const onGoogleLogin = useGoogleLoginUtils({ onSuccessJump: pageReturn });

  return (
    <div className={styles['page-register']}>
      <div className={styles['page-title']}>Create your account</div>

      {/* 使用 Suspense 包裹 SearchParamsWrapper */}
      <Suspense fallback={<div></div>}>
        <SearchParamsWrapper>
          {(searchParams) => {
            // 当 searchParams 可用时调用处理函数
            handleSearchParams(searchParams);
            return null;
          }}
        </SearchParamsWrapper>
      </Suspense>

      <Form
        form={form}
        onFinish={onFinish}
        layout={'vertical'}
        footer={
          <div>
            <div className={styles['choose-subscribe']}>
              <div className={styles['title']}>I prefer to receive:</div>
              <div className={`${styles['checkout-item']}`} style={{ paddingBottom: '8px' }}>
                <Checkbox 
                  checked={checkedWeeklyOffers}
                  onChange={onChangeWeeklyOffers}
                  shape={'square'}
                  iconSize={16}
                  style={{ margin: '2px 10px 0 0', flexShrink: 0 }}
                ></Checkbox>
                <div>
                  <div className={styles['checkout-item-title']}>Weekly Offers (3 Times per Week)</div>
                  <div className={styles['checkout-item-desc']}>Free Shipping Offers, Weekly Flash Deals, Birthday Gifts, etc.</div>
                </div>
              </div>

              <div className={`${styles['checkout-item']} margin-top-16`}>
                <Checkbox 
                  checked={checkedDaily}
                  onChange={onChangeDaily}
                  shape={'square'}
                  iconSize={16}
                  style={{ margin: '2px 10px 0 0', flexShrink: 0 }}
                ></Checkbox>
                <div>
                  <div className={styles['checkout-item-title']}>Crazy Deal of the Day (Daily)</div>
                  <div className={styles['checkout-item-desc']}>Heavily Reduced Items for 24 Hours</div>
                </div>
              </div>
            </div>

            <div className={styles['agree-terms-service']}>
              <Checkbox 
                checked={agreeTermsCondition}
                onChange={onChangeAgreeTermsCondition}
                shape={'square'}
                iconSize={16}
                style={{ marginRight: '10px', flexShrink: 0 }}
              ></Checkbox>
              <div className={styles['terms-title']}>I agree to CrazySales&apos;</div>
              <div className={styles['terms-link']} onClick={hendleViewTerms}>Terms and Conditions</div>
            </div>

            <Button 
              nativeType='submit' 
              type='primary' 
              style={{ fontWeight: '700' }}
              loading={loading}
              loadingText="Sign up"
              block
            >
              Sign up
            </Button>

            <div className={styles['register-tips']}>
              Already have an account? &nbsp;
              <Link href='/login' style={{ color: '#319EFF' }}>
                Login
              </Link>
            </div>

            <Divider style={{ margin: '6px auto', color: '#333333' }}>OR</Divider>

            <div className={styles['google-login']} onClick={() => onGoogleLogin()}>
              <Image
                alt={'google logo'}
                width={32}
                height={32}
                className={styles['google-icon']}
                src={'https://img.crazysales.com.au/images/Sign-Google.png'} />
              <div className={styles['google-login-text']}>Sign in with Google</div>
            </div>
          </div>
        }
      >
        {/* <Form.Item
          rules={[{ required: true, message: 'Your Name is required' }]}
          name='name'
          label=''
        >
          <Input placeholder='Your Name (required)' maxLength={225} />
        </Form.Item> */}
        <Form.Item
          rules={[
            { required: true, message: `Email Address is required`},
            { type: 'email', message: `Invalid Email Address`},
          ]}
          name='email'
          label=''
        >
          <Input placeholder='Email address' maxLength={225} />
        </Form.Item>
        <Form.Item
          rules={[
            { required: true, message: `The password must contain at least 6 characters`},
            { min: 6, message: `The password must contain at least 6 characters` },
          ]}
          name='password'
          label=''
        >
          <Input 
            placeholder='Password (required)' 
            type={passwordPreview ? 'text' : 'password'} 
            maxLength={64}
            suffix={
              <Image
                alt={'google logo'}
                width={20}
                height={20}
                className={styles['preview-icon']}
                src={`${process.env.IMAGE_DOMAIN}/login/${passwordPreview ? 'preview-open.png' : 'preview-close.png'}`}
                onClick={() => setPasswordPreview(!passwordPreview)} />
            } />
        </Form.Item>
        <Form.Item
          rules={[
            { required: true, message: `The password must contain at least 6 characters` },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords are not identical'));
              },
            }),
          ]}
          name='confirmPassword'
          label=''
        >
          <Input 
            placeholder='Confirm Password (required)'
            type={comfirmPasswordPreview ? 'text' : 'password'} 
            maxLength={64}
            suffix={
              <Image
                alt={'google logo'}
                width={20}
                height={20}
                className={styles['preview-icon']}
                src={`${process.env.IMAGE_DOMAIN}/login/${comfirmPasswordPreview ? 'preview-open.png' : 'preview-close.png'}`}
                onClick={() => setComfirmPasswordPreview(!comfirmPasswordPreview)} />
            }
          />
        </Form.Item>

        {
          !!(recaptchaSiteKey) &&
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={recaptchaSiteKey}
            type={'audio'}
            onChange={handleRecaptchaChange}
          />
        }
      </Form>
    </div>
  )
}

export default function AccountRegister() {
  return (
    <GoogleOAuthProvider clientId={process.env.GOOGLE_CLIENT_ID} >
      <RegistContent />
    </GoogleOAuthProvider>
  )
}